import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import OfferHeadlineLink from "@/components/OfferHeadlineLink";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Shield,
  Sparkles,
  FileText,
  Target,
  Brain,
  Compass,
  BarChart3,
  Loader2,
  RefreshCw,
  Repeat,
  TrendingUp,
  Bot,
} from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { OFFER } from "@/components/OfferCrumbs";
import LaunchOfferCountdown from "@/components/LaunchOfferCountdown";
import {
  INR_LIST_PRICE,
  INR_LIST_PRICE_LABEL,
  INR_OFFER_PRICE,
  OFFER_COUPON_CODE,
  OFFER_ENDS_LABEL,
  
  OFFER_IS_FLASH,
  OFFER_NAME,

  INR_OFFER_PRICE_LABEL,
  USD_LIST_PRICE_LABEL,
  USD_OFFER_PRICE_LABEL,
  formatInr,
} from "@/config/pricing";
import { calculatePrice, formatMoney } from "@/pricing";
import { supabase } from "@/integrations/supabase/client";
import { useBenefits } from "@/hooks/useBenefits";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { getStoredReferralCode } from "@/lib/referral";
import { getStoredAffiliateCode } from "@/lib/affiliate";
import { setPendingPayment } from "@/lib/pendingPayment";
import { DEFAULT_POST_AUTH_ROUTE, sanitizeReturnTo } from "@/lib/returnTo";
import { getSignupCoupon, storeSignupCoupon } from "@/lib/signupCoupon";
import { ga4BeginCheckout } from "@/lib/ga4";

declare global {
  interface Window {
    Razorpay: any;
    paypal: any;
  }
}

const FEATURES = [
  { icon: Brain, text: "AI-Powered Career Clarity 360 Assessment" },
  { icon: Compass, text: "Purpose & Career Fit Profile" },
  { icon: Target, text: "Strengths & Future Skills Analysis" },
  { icon: BarChart3, text: "Career Match & Learning Pathways" },
  { icon: FileText, text: "Personal Career Intelligence Report (PDF)" },
  { icon: Bot, text: "AI Career Coach & Personalised Insights" },
  { icon: Repeat, text: "5 Career Check-ins Across 12 Months" },
  { icon: TrendingUp, text: "Continuous growth tracking dashboard" },
];

// Prices live in @/config/pricing (mirrored server-side in
// supabase/functions/_shared/pricing.ts). Never hardcode a price here.
const BASE_PRICE = INR_LIST_PRICE;

type Region = "IN" | "INTL";


const Payment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [region, setRegion] = useState<Region>("IN");
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalMode, setPaypalMode] = useState<"sandbox" | "live" | null>(null);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const [paypalAttempt, setPaypalAttempt] = useState(0);
  const paypalContainerRef = useRef<HTMLDivElement | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const signupCouponRef = useRef(getSignupCoupon(window.location.search));
  const couponRequestRef = useRef(0);
  const activeCurrencyRef = useRef("INR");

  // Guard: if the signed-in user already has a successful payment (or is an
  // admin), bounce them to the learner's canonical home instead of showing
  // the paywall again. This covers the case where a paid user clicks an old
  // "Pay" link, types /payment directly, or is sent here via a stale email.
  // Uses DEFAULT_POST_AUTH_ROUTE so this destination stays in step with
  // post-auth routing rather than drifting as a second hardcoded landing.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    (async () => {
      const [{ data: roles }, { data: pays }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        supabase
          .from("payments")
          .select("id")
          .eq("user_id", user.id)
          .eq("payment_status", "success")
          .limit(1),
      ]);
      if (cancelled) return;
      const isAdmin = !!roles?.some((r) => r.role === "admin");
      const isPaid = (pays?.length ?? 0) > 0;
      if (isAdmin || isPaid) {
        storeSignupCoupon("");
        // Honour the page they were trying to reach before the paywall
        // intercepted them, falling back to the learner's home.
        const next = new URLSearchParams(window.location.search).get("next");
        navigate(sanitizeReturnTo(next, DEFAULT_POST_AUTH_ROUTE), {
          replace: true,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, navigate]);
  const [couponError, setCouponError] = useState<string | null>(null);
  // The code is real but this learner's application hasn't been approved yet.
  const [needsApplication, setNeedsApplication] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    label: string;
    final_price: number;
    discount_applied: number;
    /** Currency the quote was priced in — a quote never crosses regions. */
    currency: string;
    list_price: number;
    discount_percent: number;
  } | null>(null);
  const [form, setForm] = useState({
    user_name: "",
    email: "",
    phone: "",
    age: "",
    school: "",
    city: "",
    scholarship_code: signupCouponRef.current,
  });

  // DPDP Act. A parent buying for someone under 18 tells us so here, and the
  // child's date of birth is what puts that learner behind the consent gate.
  // Payment success then routes to verification and consent — never straight
  // into the walk.
  const [purchaser, setPurchaser] = useState({
    buying_for_minor: false,
    child_name: "",
    child_dob: "",
    relationship: "parent",
  });

  const updateField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  /** Whole years from a yyyy-mm-dd string, or null when it is unusable. */
  const yearsFrom = (dob: string): number | null => {
    const d = new Date(`${dob}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let years = now.getUTCFullYear() - d.getUTCFullYear();
    const m = now.getUTCMonth() - d.getUTCMonth();
    if (m < 0 || (m === 0 && now.getUTCDate() < d.getUTCDate())) years -= 1;
    return years < 0 || years > 120 ? null : years;
  };

  /**
   * Records the purchase and the child's date of birth before we take money, so
   * the consent chain starts at the purchase rather than being reconstructed
   * afterwards. Returns false when the details are unusable.
   */
  const registerChild = async (): Promise<boolean> => {
    if (!purchaser.buying_for_minor) return true;
    const age = yearsFrom(purchaser.child_dob);
    if (!purchaser.child_name.trim()) {
      toast.error("Please enter the young person's name.");
      return false;
    }
    if (age == null) {
      toast.error("Please enter a valid date of birth.");
      return false;
    }
    const { error } = await supabase.functions.invoke("parent-verification", {
      body: {
        action: "register_child",
        child_name: purchaser.child_name.trim(),
        child_dob: purchaser.child_dob,
        relationship: purchaser.relationship,
        purchaser_name: form.user_name,
        purchaser_email: form.email,
        purchaser_phone: form.phone,
        currency,
        amount_minor: Math.round(finalPrice * 100),
      },
    });
    if (error) {
      toast.error("We couldn't save the guardian details. Please try again.");
      return false;
    }
    return true;
  };

  // Auto-fill name + email from the signed-in user (Google OAuth, email signup,
  // or a previously remembered payment email). We only fill blanks so we don't
  // clobber anything the user has manually typed.
  useEffect(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    const metaName =
      (meta.full_name as string) ||
      (meta.name as string) ||
      [meta.given_name, meta.family_name].filter(Boolean).join(" ").trim() ||
      "";
    const authEmail = user?.email || "";
    let rememberedEmail = "";
    try {
      rememberedEmail = localStorage.getItem("wekit_payment_email") || "";
    } catch {
      /* ignore */
    }
    setForm((prev) => ({
      ...prev,
      user_name: prev.user_name || metaName,
      email: prev.email || authEmail || rememberedEmail,
    }));
  }, [user]);



  /** Engine region for the selected gateway. */
  const pricingRegion = region === "IN" ? "IN" : "US";
  /** List price + currency for the selected region, straight from the engine. */
  const regionQuote = calculatePrice({ region: pricingRegion });
  const currency = regionQuote.currency;
  activeCurrencyRef.current = currency;
  const listPrice = regionQuote.listPrice;
  const money = (amount: number) => formatMoney(amount, currency);

  const finalPrice = appliedCoupon ? appliedCoupon.final_price : listPrice;

  /** Account-based entitlements from the Access & Benefits Engine. */
  const { autoBenefitId, autoBenefitLabel } = useBenefits(pricingRegion);

  const applyCoupon = async (codeOverride?: string) => {
    const code = (codeOverride ?? form.scholarship_code).trim().toUpperCase();
    if (codeOverride) updateField("scholarship_code", codeOverride);
    setCouponError(null);
    if (!code) {
      setCouponError("Please enter a code.");
      return;
    }
    if (!/^[A-Z0-9_-]{3,32}$/.test(code)) {
      setCouponError(
        "Code format looks wrong. Use 3–32 letters, numbers, hyphens, or underscores.",
      );
      return;
    }
    setCouponLoading(true);
    const requestId = ++couponRequestRef.current;
    const requestCurrency = currency;
    try {
      const { data, error } = await supabase.functions.invoke("validate-coupon", {
        body: { code, region: pricingRegion },
      });
      let payload: any = data;
      if (!payload && error && (error as any).context?.json) {
        try {
          payload = await (error as any).context.json();
        } catch {
          /* ignore */
        }
      }
      if (requestId !== couponRequestRef.current || requestCurrency !== activeCurrencyRef.current) return;
      if (!payload?.ok) {
        setCouponError(payload?.error || "We couldn't verify that code. Please try again.");
        setNeedsApplication(payload?.reason === "awaiting_approval");
        setAppliedCoupon(null);
      } else {
        setNeedsApplication(false);
        const quoteCurrency = payload.currency || currency;
        setAppliedCoupon({
          code: payload.coupon.code,
          label: payload.coupon.label,
          final_price: payload.final_price,
          discount_applied: payload.discount_applied,
          currency: quoteCurrency,
          list_price: payload.base_price ?? listPrice,
          discount_percent: payload.discount_percent ?? 0,
        });
        toast.success(
          payload.final_price === 0
            ? "100% scholarship applied — no payment needed!"
            : `Code applied — you save ${formatMoney(payload.discount_applied, quoteCurrency)}`,
        );
      }
    } catch (e) {
      setCouponError("We couldn't reach the server. Please check your connection and try again.");
    } finally {
      if (requestId === couponRequestRef.current) setCouponLoading(false);
    }
  };

  // Auto-apply a stored referral code once on mount (best-one-wins: the user
  // can still type another code manually and it replaces this one).
  const autoAppliedRef = useRef(false);
  useEffect(() => {
    if (signupCouponRef.current) return;
    if (autoAppliedRef.current) return;
    if (appliedCoupon) return;
    const stored = getStoredReferralCode();
    if (!stored) return;
    autoAppliedRef.current = true;
    applyCoupon(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Account-based benefits (approved scholarship, partner or institution
  // pricing) need no code: the Access & Benefits Engine resolves the
  // entitlement and it is applied through the same validated path a typed code
  // uses, so the gateway still charges exactly what the engine quotes.
  const benefitAppliedRef = useRef(false);
  useEffect(() => {
    if (benefitAppliedRef.current) return;
    if (appliedCoupon || autoAppliedRef.current || signupCouponRef.current) return;
    if (!autoBenefitId) return;
    benefitAppliedRef.current = true;
    applyCoupon(autoBenefitId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBenefitId]);


  // A quote belongs to one currency. Switching region invalidates it, so the
  // learner re-applies and sees the price that region will actually charge.
  useEffect(() => {
    setAppliedCoupon((prev) =>
      prev && prev.currency !== currency ? null : prev,
    );
    setCouponError(null);
  }, [currency]);

  // The same saved code is independently validated for each selected currency.
  useEffect(() => {
    if (!user?.id || !signupCouponRef.current) return;
    void applyCoupon(signupCouponRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currency]);

  const removeCoupon = () => {
    signupCouponRef.current = "";
    storeSignupCoupon("");
    couponRequestRef.current += 1;
    setCouponLoading(false);
    setAppliedCoupon(null);
    setCouponError(null);
    updateField("scholarship_code", "");
  };

  /**
   * International checkout with an approved full scholarship. PayPal cannot
   * charge zero, so the server records the unlock and we go straight through —
   * the same shape the India checkout already uses.
   */
  const handleInternationalFree = async () => {
    if (!formValidForPaypal()) return;
    setLoading(true);
    try {
      if (!(await registerChild())) {
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke(
        "create-paypal-order",
        {
          body: {
            ...form,
            scholarship_code: appliedCoupon?.code ?? "",
            affiliate_code: getStoredAffiliateCode() ?? "",
          },
        },
      );
      if (error || !data?.free) {
        toast.error("We couldn't unlock that just now. Please try again.");
        setLoading(false);
        return;
      }
      localStorage.setItem("wekit_payment_email", form.email);
      storeSignupCoupon("");
      toast.success("Scholarship applied — your assessment is now unlocked.");
      navigate("/payment-success");
    } catch (e) {
      console.error("international free unlock failed:", e);
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!form.user_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email, and phone number.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(form.phone.replace(/\D/g, ""))) {
      toast.error("Please enter a valid 10-digit Indian phone number.");
      return;
    }

    setLoading(true);
    ga4BeginCheckout(finalPrice, currency, "razorpay");

    if (!(await registerChild())) {
      setLoading(false);
      return;
    }

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "create-razorpay-order",
        {
          body: { ...form, affiliate_code: getStoredAffiliateCode() || undefined },
        }
      );

      if (error || !data) {
        let reason = error?.message ?? "Unknown error";
        const ctx = (error as { context?: Response } | null)?.context;
        if (ctx && typeof ctx.text === "function") {
          try {
            const raw = await ctx.text();
            const parsed = JSON.parse(raw) as { error?: string };
            reason = parsed.error || raw || reason;
          } catch {
            /* keep fallback reason */
          }
        }
        console.error("create-razorpay-order failed:", reason);
        toast.error(reason);
        setLoading(false);
        return;
      }


      // Full scholarship: server already unlocked the assessment, skip Razorpay
      if (data.free) {
        storeSignupCoupon("");
        localStorage.setItem("wekit_payment_email", form.email);
        toast.success("Scholarship applied — your assessment is now unlocked.");
        navigate("/payment-success");
        return;
      }

      if (!data.order_id) {
        toast.error("Failed to create payment order. Please try again.");
        setLoading(false);
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "WeKIT™ Mentoring",
        description: "AI Career Discovery Assessment",
        order_id: data.order_id,
        prefill: {
          name: form.user_name,
          email: form.email,
          contact: form.phone,
        },
        notes: {
          product: "WeKIT™ AI Career Discovery Assessment",
        },
        theme: {
          color: "#00665B",
        },
        handler: async (response: any) => {
          // Payment status is updated authoritatively by the razorpay-webhook
          // edge function using the service role; the client must not write
          // payment records directly.

          // Store payment info for gating
          localStorage.setItem("wekit_payment_email", form.email);
          localStorage.setItem("wekit_payment_order", response.razorpay_order_id);
          setPendingPayment({
            orderId: response.razorpay_order_id,
            provider: "razorpay",
            paymentId: response.razorpay_payment_id,
            email: form.email,
          });

          navigate("/payment-success");
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info("Payment cancelled. You can try again when ready.");
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response: any) => {
        setLoading(false);
        const reason = response?.error?.description || "Payment could not be completed.";
        const orderId = response?.error?.metadata?.order_id || "";
        toast.error(`Payment failed: ${reason}`);
        navigate(`/payment-failure?reason=${encodeURIComponent(reason)}&order=${encodeURIComponent(orderId)}`);
      });
      razorpay.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  // --- PayPal flow ---
  const formValidForPaypal = () => {
    if (!form.user_name || !form.email) {
      toast.error("Please fill in your name and email.");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  const loadPaypalSdk = async (): Promise<boolean> => {
    if (window.paypal) return true;
    try {
      const { data } = await supabase.functions.invoke("paypal-config", {
        method: "GET",
      });
      if (!data?.client_id) {
        toast.error("PayPal is not configured yet.");
        return false;
      }
      setPaypalMode(data.mode === "sandbox" ? "sandbox" : "live");
      return await new Promise<boolean>((resolve) => {
        const s = document.createElement("script");
        s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(
          data.client_id,
        )}&currency=USD&intent=capture`;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
      });
    } catch (e) {
      console.error("PayPal SDK load error:", e);
      return false;
    }
  };

  // Render PayPal buttons when International region is active.
  // A network hiccup here leaves a paying learner staring at an empty box, so
  // a failure is surfaced with a retry rather than swallowed.
  useEffect(() => {
    let cancelled = false;
    if (region !== "INTL") {
      setPaypalReady(false);
      setPaypalError(null);
      if (paypalContainerRef.current) paypalContainerRef.current.innerHTML = "";
      return;
    }

    setPaypalError(null);
    (async () => {
      const ok = await loadPaypalSdk();
      if (cancelled) return;
      if (!ok || !window.paypal || !paypalContainerRef.current) {
        setPaypalReady(false);
        setPaypalError("We could not load the payment options. Check your connection and try again.");
        return;
      }
      paypalContainerRef.current.innerHTML = "";
      setPaypalReady(true);


      window.paypal
        .Buttons({
          style: { layout: "vertical", color: "blue", shape: "pill", label: "paypal" },
          onClick: (_data: any, actions: any) => {
            if (!formValidForPaypal()) return actions.reject();
            ga4BeginCheckout(finalPrice, currency, "paypal");
            return actions.resolve();
          },
          createOrder: async () => {
            // DPDP: the guardian/child details are recorded before money moves.
            if (!(await registerChild())) throw new Error("guardian_details_invalid");
            const { data, error } = await supabase.functions.invoke(
              "create-paypal-order",
              {
                body: {
                  ...form,
                  scholarship_code: appliedCoupon?.code ?? "",
                  affiliate_code: getStoredAffiliateCode() ?? "",
                },
              },
            );
            if (error || !data?.order_id) {
              toast.error("Could not create PayPal order. Please try again.");
              throw new Error("create-paypal-order failed");
            }
            return data.order_id;
          },
          onApprove: async (data: any) => {
            setLoading(true);
            try {
              const { data: cap, error } = await supabase.functions.invoke(
                "capture-paypal-order",
                { body: { order_id: data.orderID } },
              );
              if (error || !cap?.ok) {
                toast.error("Payment could not be captured. Please contact support.");
                setLoading(false);
                return;
              }
              localStorage.setItem("wekit_payment_email", form.email);
              localStorage.setItem("wekit_payment_order", data.orderID);
              setPendingPayment({
                orderId: data.orderID,
                provider: "paypal",
                email: form.email,
              });
              toast.success("Payment received. Thank you.");
              navigate("/payment-success");
            } catch (e) {
              console.error("PayPal capture error:", e);
              toast.error("Something went wrong. Please try again.");
              setLoading(false);
            }
          },
          onError: (err: any) => {
            console.error("PayPal error:", err);
            toast.error("PayPal payment failed. Please try again.");
          },
          onCancel: () => {
            toast.info("Payment cancelled. You can try again when ready.");
          },
        })
        .render(paypalContainerRef.current);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `appliedCoupon` is a dependency because the amount PayPal is asked to
    // create the order for changes with it.
  }, [
    region,
    paypalAttempt,
    appliedCoupon,
    form.user_name,
    form.email,
    form.phone,
    form.age,
    form.school,
    form.city,
    purchaser,
  ]);


  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="secondary" className="gap-1 text-xs">
            <Shield className="h-3 w-3" /> Secure Payment
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-3">
            Discover Your Purpose with WeKIT™
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
            AI Career Discovery Assessment — your AI-powered Career Blueprint in 30–60 minutes
          </p>

          {OFFER_IS_FLASH && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 max-w-2xl mx-auto"
          >
            <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/15 via-accent/10 to-primary/15 px-5 py-4 sm:px-6 sm:py-5 backdrop-blur">
              <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
              <div className="absolute -bottom-12 -left-12 h-32 w-32 rounded-full bg-accent/20 blur-3xl" />
              <div className="relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-center sm:text-left">
                <Badge className="bg-primary text-primary-foreground border-0 font-semibold shrink-0">
                  <Sparkles className="h-3 w-3 mr-1" /> {OFFER.name}
                </Badge>
                <div className="flex flex-wrap items-baseline justify-center gap-2">
                  <span className="font-display text-2xl font-bold text-foreground">
                    {OFFER.now}
                  </span>
                  <span className="text-sm text-muted-foreground line-through">{OFFER.was}</span>
                  <span className="text-sm font-semibold text-primary">{OFFER.save}</span>
                </div>
                <LaunchOfferCountdown compact />
              </div>
              <p className="relative mt-2 text-center text-xs text-muted-foreground">
                {OFFER.endsLabel} · apply code {OFFER_COUPON_CODE} at checkout.
              </p>
            </div>
          </motion.div>
          )}
        </motion.div>



        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* What you get */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="border-border/50 bg-card/50 backdrop-blur h-full">
              <CardHeader>
                <CardTitle className="text-xl text-foreground">
                  What you'll get
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <f.icon className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-foreground text-sm min-w-0 break-words">{f.text}</span>
                  </div>
                ))}

                <div className="mt-6 pt-6 border-t border-border/50">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-foreground">
                      {USD_LIST_PRICE_LABEL} / {INR_LIST_PRICE_LABEL}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      per learner / year
                    </span>
                  </div>
                  {OFFER_IS_FLASH && (
                    <div className="mt-2">
                      <OfferHeadlineLink
                        surface="checkout"
                        showCue={false}
                        className="text-sm font-bold leading-snug text-primary"
                      />
                    </div>
                  )}
                  {OFFER_IS_FLASH && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Use code <span className="font-semibold text-foreground">{OFFER_COUPON_CODE}</span> for the {OFFER_NAME} price of {USD_OFFER_PRICE_LABEL} / {INR_OFFER_PRICE_LABEL} until {OFFER_ENDS_LABEL}.
                  </p>
                  )}
                  <div className="mt-2">
                  </div>

                  <p className="text-xs text-muted-foreground mt-1">
                    Includes 12 months of AI-powered career guidance with 5 assessments, continuous progress tracking, personalised reports, and Career Intelligence Dashboard access.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Payment form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="border-primary/20 bg-card/80 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl text-foreground">
                  Your Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Region toggle */}
                <div className="rounded-xl border border-border/60 bg-muted/30 p-1 grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    onClick={() => setRegion("IN")}
                    className={`h-10 rounded-lg text-sm font-medium transition-colors ${
                      region === "IN"
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🇮🇳 India · {INR_LIST_PRICE_LABEL}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegion("INTL")}
                    className={`h-10 rounded-lg text-sm font-medium transition-colors ${
                      region === "INTL"
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    🌍 International · {USD_LIST_PRICE_LABEL}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                  {region === "IN"
                    ? "Pay in INR via UPI, cards, netbanking (Razorpay)"
                    : "Pay in USD via PayPal or international card"}
                </p>

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={form.user_name}
                    onChange={(e) => updateField("user_name", e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    maxLength={255}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-foreground">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    maxLength={15}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="age" className="text-foreground">Age</Label>
                    <Input
                      id="age"
                      placeholder="e.g. 17"
                      value={form.age}
                      onChange={(e) => updateField("age", e.target.value)}
                      maxLength={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-foreground">City</Label>
                    <Input
                      id="city"
                      placeholder="Your city"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      maxLength={100}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school" className="text-foreground">School / College</Label>
                  <Input
                    id="school"
                    placeholder="Your school or college"
                    value={form.school}
                    onChange={(e) => updateField("school", e.target.value)}
                    maxLength={200}
                  />
                </div>

                {/* DPDP Act. When the Passport is for someone under 18, the law
                    asks for verifiable parental consent before we process their
                    responses — so we ask who the young person is up front. */}
                <div className="rounded-2xl border border-border/60 bg-muted/20 p-4 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 rounded border-border accent-primary"
                      checked={purchaser.buying_for_minor}
                      onChange={(e) =>
                        setPurchaser((p) => ({ ...p, buying_for_minor: e.target.checked }))
                      }
                      aria-describedby="minor-help"
                    />
                    <span className="text-sm text-foreground">
                      I'm purchasing the WeKIT Career Clarity for a young person under 18
                      <span id="minor-help" className="block text-xs text-muted-foreground mt-1">
                        You'll verify you're their parent or guardian and give
                        consent right after payment. It takes about a minute.
                      </span>
                    </span>
                  </label>

                  {purchaser.buying_for_minor && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="child_name" className="text-foreground">
                          Their name *
                        </Label>
                        <Input
                          id="child_name"
                          placeholder="Their full name"
                          value={purchaser.child_name}
                          onChange={(e) =>
                            setPurchaser((p) => ({ ...p, child_name: e.target.value }))
                          }
                          maxLength={120}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="child_dob" className="text-foreground">
                          Their date of birth *
                        </Label>
                        <Input
                          id="child_dob"
                          type="date"
                          max={new Date().toISOString().slice(0, 10)}
                          value={purchaser.child_dob}
                          onChange={(e) =>
                            setPurchaser((p) => ({ ...p, child_dob: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="relationship" className="text-foreground">
                          Your relationship to them
                        </Label>
                        <select
                          id="relationship"
                          className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                          value={purchaser.relationship}
                          onChange={(e) =>
                            setPurchaser((p) => ({ ...p, relationship: e.target.value }))
                          }
                        >
                          <option value="parent">Parent</option>
                          <option value="legal_guardian">Legal guardian</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                {(
                  <div className="space-y-2">
                    <Label htmlFor="scholarship" className="text-foreground">
                      Coupon Code / Scholarship / NGO Code (optional)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="scholarship"
                        placeholder="Enter code if you have one"
                        value={form.scholarship_code}
                        onChange={(e) => {
                          signupCouponRef.current = e.target.value.toUpperCase();
                          storeSignupCoupon(e.target.value);
                          updateField("scholarship_code", e.target.value.toUpperCase());
                          if (appliedCoupon) setAppliedCoupon(null);
                          if (couponError) setCouponError(null);
                        }}
                        maxLength={50}
                        disabled={!!appliedCoupon}
                        aria-invalid={!!couponError}
                        aria-describedby={couponError ? "scholarship-error" : undefined}
                      />
                      {appliedCoupon ? (
                        <Button type="button" variant="outline" onClick={removeCoupon}>
                          Remove
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => applyCoupon()}
                          disabled={couponLoading || !form.scholarship_code.trim()}
                        >
                          {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                        </Button>
                      )}
                    </div>
                    {autoBenefitLabel && appliedCoupon && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Applied automatically from your account: {autoBenefitLabel}. No code needed.
                      </p>
                    )}
                    {couponError && !appliedCoupon && (
                      <p
                        id="scholarship-error"
                        role="alert"
                        className="text-xs text-destructive mt-1"
                      >
                        {couponError}
                      </p>
                    )}
                    {needsApplication && !appliedCoupon && (
                      <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="mt-2 min-h-11"
                      >
                        <Link to="/scholarship">Apply for a scholarship</Link>
                      </Button>
                    )}
                    {appliedCoupon && appliedCoupon.code === "WCC3604INDIA" ? (
                      <div className="rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 space-y-2">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                          <Sparkles className="h-3.5 w-3.5" />
                          Leadership Preview Access Applied
                        </div>
                        <h3 className="font-display text-lg font-semibold text-foreground">
                          Welcome to the WeKIT™ Career Clarity 360 Leadership Preview
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          This complimentary access has been provided for selected leaders to
                          experience our AI-powered Career Intelligence platform. Your total is now
                          <span className="font-semibold text-foreground"> ₹0</span> — proceed
                          directly to your assessment, no payment required.
                        </p>
                      </div>
                    ) : appliedCoupon && (
                      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                        <div className="font-medium text-foreground">
                          {appliedCoupon.label}
                        </div>
                        {appliedCoupon.final_price === 0 ? (
                          <div className="text-muted-foreground text-xs mt-0.5">
                            100% scholarship — no payment needed
                          </div>
                        ) : (
                          <dl className="mt-1.5 space-y-1 text-xs">
                            <div className="flex justify-between gap-3">
                              <dt className="text-muted-foreground">MRP</dt>
                              <dd className="text-muted-foreground line-through">
                                {money(appliedCoupon.list_price)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                              <dt className="text-muted-foreground">Discount</dt>
                              <dd className="text-foreground">
                                −{money(appliedCoupon.discount_applied)} (
                                {appliedCoupon.discount_percent}%)
                              </dd>
                            </div>
                            <div className="flex justify-between gap-3 pt-1 border-t border-border/50">
                              <dt className="font-medium text-foreground">You pay</dt>
                              <dd className="font-semibold text-foreground">
                                {money(appliedCoupon.final_price)}
                              </dd>
                            </div>
                            <div className="flex justify-between gap-3">
                              <dt className="text-muted-foreground">You save</dt>
                              <dd className="text-primary">
                                {money(appliedCoupon.discount_applied)}
                              </dd>
                            </div>
                          </dl>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {region === "IN" ? (
                  <>
                    <Button
                      onClick={handlePayment}
                      disabled={loading}
                      className="w-full h-14 text-base font-semibold mt-4"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : finalPrice === 0 ? (
                        <>
                          <Sparkles className="h-5 w-5" />
                          Unlock My Full Career Report — Free
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-5 w-5" />
                          Unlock My Full Career Report — {formatInr(finalPrice)}
                        </>
                      )}
                    </Button>
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-2">
                      <Shield className="h-3 w-3" />
                      <span>Secured by Razorpay · 256-bit encryption</span>
                    </div>
                  </>
                ) : finalPrice === 0 ? (
                  <>
                    <Button
                      onClick={handleInternationalFree}
                      disabled={loading}
                      className="w-full h-14 text-base font-semibold mt-4"
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-5 w-5" />
                          Unlock My Full Career Report — Free
                        </>
                      )}
                    </Button>
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mt-2">
                      <Shield className="h-3 w-3" />
                      <span>Your scholarship covers the full cost</span>
                    </div>
                  </>
                ) : (
                  <div className="mt-4 space-y-3">
                    {paypalMode === "sandbox" && (
                      <div className="rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-foreground flex items-center gap-2">
                        <span className="inline-flex h-2 w-2 rounded-full bg-[hsl(var(--warning))] animate-pulse" />
                        <span className="font-semibold">PayPal Sandbox mode</span>
                        <span className="text-muted-foreground">— test transactions only, no real money will be charged. Use sandbox buyer accounts to pay.</span>
                      </div>
                    )}
                    <div className="rounded-xl border border-border/60 bg-background/60 p-4">
                      <div className="flex items-baseline justify-between mb-3">
                        <span className="text-sm text-muted-foreground">Total</span>
                        <span className="font-display text-2xl font-bold text-foreground">
                          {appliedCoupon && (
                            <span className="mr-2 text-base font-normal text-muted-foreground line-through">
                              {money(appliedCoupon.list_price)}
                            </span>
                          )}
                          {currency === "INR"
                            ? Math.round(finalPrice).toLocaleString("en-IN")
                            : finalPrice.toFixed(2)}{" "}

                          <span className="text-sm font-normal text-muted-foreground">
                            {currency}
                          </span>
                        </span>
                      </div>
                      <div ref={paypalContainerRef} className="min-h-[50px]" />
                      {!paypalReady && !paypalError && (
                        <div
                          className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2"
                          role="status"
                          aria-live="polite"
                        >
                          <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                          Getting your payment options ready…
                        </div>
                      )}
                      {paypalError && (
                        <div className="py-2 text-center space-y-2" role="alert">
                          <p className="text-xs text-muted-foreground">{paypalError}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-11"
                            onClick={() => setPaypalAttempt((a) => a + 1)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1.5" aria-hidden="true" />
                            Try again
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Shield className="h-3 w-3" />
                      <span>
                        Secured by PayPal · Pay with PayPal or card
                        {paypalMode === "sandbox" && " · sandbox"}
                      </span>
                    </div>
                  </div>
                )}


              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Payment;
